'use client';
import React, { useState, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Certificate } from '@/types/certificate';
import { getCertificateById, formatDate } from '@/lib/certificateStore';
import StatusBadge from '@/components/StatusBadge';

type SearchState = 'idle' | 'loading' | 'found' | 'not-found';

export default function CertificateSearchClient() {
  const [query, setQuery] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [result, setResult] = useState<Certificate | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearchState('loading');
    setResult(null);

    try {
      const cert = await getCertificateById(query.trim());
      if (cert) {
        setResult(cert);
        setSearchState('found');
      } else {
        setSearchState('not-found');
      }
    } catch {
      setSearchState('not-found');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setQuery('');
    setSearchState('idle');
    setResult(null);
    inputRef.current?.focus();
  };

  return (
    <>
      {/* Hero Search Section */}
      <section className="gradient-hero py-16 px-6 lg:px-10 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-5"
          style={{ background: 'radial-gradient(ellipse at 70% 50%, #D4A017 0%, transparent 60%)' }}
        />
        <div className="max-w-screen-2xl mx-auto relative">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-blue-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border border-white/20">
              <ShieldCheckIcon className="w-3.5 h-3.5" />
              Official Certificate Verification Portal
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Verify ISO Certificate
            </h1>
            <p className="text-blue-200 text-base mb-8">
              Enter the Certificate Identification Number to verify authenticity,
              check validity, and view certification details.
            </p>

            {/* Search Bar */}
            <div className="flex gap-3 bg-white/10 border border-white/20 rounded-xl p-2 backdrop-blur-sm">
              <div className="flex-1 flex items-center gap-3 bg-white rounded-lg px-4">
                <MagnifyingGlassIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter Certificate ID"
                  className="flex-1 py-3 text-sm text-foreground placeholder:text-muted-foreground bg-transparent focus:outline-none font-medium"
                  autoComplete="off"
                  spellCheck={false}
                  suppressHydrationWarning
                />
                {query && (
                  <button
                    onClick={handleClear}
                    className="p-1 rounded-md hover:bg-muted transition-colors"
                    aria-label="Clear search"
                  >
                    <XCircleIcon className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                disabled={!query.trim() || searchState === 'loading'}
                className="btn-accent px-6 py-3 text-sm rounded-lg disabled:opacity-60"
              >
                {searchState === 'loading' ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4A8 8 0 014 12z" />
                    </svg>
                    Searching
                  </span>
                ) : (
                  'Verify'
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Area */}
      <section className="py-10 px-6 lg:px-10">
        <div className="max-w-screen-2xl mx-auto">
          <div className="max-w-3xl mx-auto">

            {/* Loading State */}
            {searchState === 'loading' && (
              <div className="card p-8 animate-fade-in">
                <div className="flex flex-col gap-4">
                  <div className="skeleton h-6 w-48" />
                  <div className="skeleton h-4 w-full" />
                  <div className="skeleton h-4 w-3/4" />
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="skeleton h-16 w-full rounded-lg" />
                    <div className="skeleton h-16 w-full rounded-lg" />
                    <div className="skeleton h-16 w-full rounded-lg" />
                    <div className="skeleton h-16 w-full rounded-lg" />
                  </div>
                </div>
              </div>
            )}

            {/* Not Found State */}
            {searchState === 'not-found' && (
              <div className="card p-10 text-center animate-scale-in">
                <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-5">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Certificate Not Found
                </h3>
                <p className="text-muted-foreground text-sm mb-2 max-w-md mx-auto">
                  No certificate matching{' '}
                  <span className="font-semibold text-foreground bg-muted px-1.5 py-0.5 rounded">
                    {query}
                  </span>{' '}
                  was found in the MOC Certification registry.
                </p>
                <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
                  Please check the certificate ID and try again. If you believe this
                  is an error, contact MOC Certification at info@moccertification.com.
                </p>
                <button
                  onClick={handleClear}
                  className="btn-outline"
                >
                  Search Again
                </button>
              </div>
            )}

            {/* Found State */}
            {searchState === 'found' && result && (
              <div className="animate-slide-up">
                {/* Status Banner */}
                <div
                  className={`rounded-xl p-4 mb-5 flex items-center gap-3 border ${
                    result.status === 'Active' ? 'bg-green-50 border-green-200'
                      : result.status === 'Expiring Soon' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  {result.status === 'Active' ? (
                    <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                  ) : result.status === 'Expiring Soon' ? (
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={`text-sm font-bold ${
                        result.status === 'Active' ? 'text-green-800'
                          : result.status === 'Expiring Soon' ? 'text-amber-800' : 'text-red-800'
                      }`}
                    >
                      {result.status === 'Active' ? 'Certificate is Valid and Active'
                        : result.status === 'Expiring Soon' ? 'Certificate is Expiring Soon' : 'Certificate has Expired'}
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${
                        result.status === 'Active' ? 'text-green-700'
                          : result.status === 'Expiring Soon' ? 'text-amber-700' : 'text-red-700'
                      }`}
                    >
                      {result.status === 'Active'
                        ? `This certificate is currently valid and recognized by MOC Certification.`
                        : result.status === 'Expiring Soon'
                        ? `This certificate expires on ${formatDate(result.expiryDate)}. Renewal is recommended immediately.`
                        : `This certificate expired on ${formatDate(result.expiryDate)} and is no longer valid.`}
                    </p>
                  </div>
                </div>

                {/* Certificate Card */}
                <div className="card overflow-hidden">
                  {/* Card Header */}
                  <div className="gradient-hero px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
                        <ShieldCheckIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-blue-200 text-xs font-semibold uppercase tracking-wider mb-0.5">
                          MOC Certification — Certificate Record
                        </p>
                        <p className="text-white text-lg font-bold font-tabular">
                          {result.certificateId}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={result.status} size="lg" />
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Certificate Holder */}
                      <div className="flex items-start gap-3 p-4 bg-secondary rounded-xl">
                        <IdentificationIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Certificate Holder
                          </p>
                          <p className="text-sm font-bold text-foreground">
                            {result.holderName}
                          </p>
                        </div>
                      </div>

                      {/* Company Name */}
                      <div className="flex items-start gap-3 p-4 bg-secondary rounded-xl">
                        <BuildingOfficeIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Registered Company
                          </p>
                          <p className="text-sm font-bold text-foreground">
                            {result.companyName}
                          </p>
                        </div>
                      </div>

                      {/* Standard */}
                      <div className="flex items-start gap-3 p-4 bg-secondary rounded-xl">
                        <DocumentTextIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Certification Standard
                          </p>
                          <p className="text-sm font-bold text-foreground">
                            {result.standard}
                          </p>
                        </div>
                      </div>

                      {/* Certificate ID */}
                      <div className="flex items-start gap-3 p-4 bg-secondary rounded-xl">
                        <IdentificationIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Certificate ID
                          </p>
                          <p className="text-sm font-bold text-foreground font-tabular">
                            {result.certificateId}
                          </p>
                        </div>
                      </div>

                      {/* Issue Date */}
                      <div className="flex items-start gap-3 p-4 bg-secondary rounded-xl">
                        <CalendarDaysIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Issue Date
                          </p>
                          <p className="text-sm font-bold text-foreground font-tabular">
                            {formatDate(result.issueDate)}
                          </p>
                        </div>
                      </div>

                      {/* Expiry Date */}
                      <div className={`flex items-start gap-3 p-4 rounded-xl ${
                        result.status === 'Active' ? 'bg-secondary' :
                        result.status === 'Expiring Soon' ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'
                      }`}>
                        <CalendarDaysIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          result.status === 'Active' ? 'text-primary' :
                          result.status === 'Expiring Soon' ? 'text-amber-600' : 'text-red-600'
                        }`} />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Expiry Date
                          </p>
                          <p className={`text-sm font-bold font-tabular ${
                            result.status === 'Active' ? 'text-foreground' :
                            result.status === 'Expiring Soon' ? 'text-amber-800' : 'text-red-800'
                          }`}>
                            {formatDate(result.expiryDate)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {result.notes && (
                      <div className="mt-5 p-4 bg-secondary rounded-xl flex items-start gap-3">
                        <InformationCircleIcon className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            Notes
                          </p>
                          <p className="text-sm text-foreground">{result.notes}</p>
                        </div>
                      </div>
                    )}

                    {/* File Download — now uses Supabase Storage public URL */}
                    {result.fileData && (
                      <div className="mt-5">
                        <a
                          href={result.fileData}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={result.fileName || `${result.certificateId}.pdf`}
                          className="btn-primary w-full justify-center py-3"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                          Download Certificate Document
                        </a>
                      </div>
                    )}

                    {/* Footer note */}
                    <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Issued by MOC Certification • Accredited ISO Certification Body
                      </p>
                      <p className="text-xs text-muted-foreground font-tabular">
                        Verified on {new Date().toLocaleDateString('en-GB')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <button
                    onClick={handleClear}
                    className="btn-outline text-sm"
                  >
                    Search Another Certificate
                  </button>
                </div>
              </div>
            )}

            {/* Idle State */}
            {searchState === 'idle' && (
              <div className="animate-fade-in">
                <div className="card p-6 mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <InformationCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">
                        How to verify a certificate
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Enter the Certificate Identification Number exactly as printed
                        on the certificate document. The format is typically{' '}
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground">
                          MOC-YYYY-NNN
                        </span>
                        . Verification is case-insensitive.
                      </p>
                    </div>
                  </div>

                  {/* Trust indicators */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { id: 'trust-1', icon: ShieldCheckIcon, title: 'Official Registry', desc: 'All results pulled directly from the MOC Certification official database' },
                      { id: 'trust-2', icon: CheckCircleIcon, title: 'Real-Time Status', desc: "Certificate validity is computed in real time against today's date" },
                      { id: 'trust-3', icon: DocumentTextIcon, title: 'Full Details', desc: 'View holder name, company, standard, dates, and download the certificate' },
                    ].map(t => (
                      <div key={t.id} className="card p-4 flex items-start gap-3">
                        <t.icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground mb-0.5">{t.title}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{t.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </>
  );
}